import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { FetchMoreOptions, FetchMoreQueryOptions, ApolloQueryResult } from 'apollo-client';

import Button from '@atlaskit/button/new';

const i18n = defineMessages({
	showMoreButton: {
		id: 'questions.pagination.show-more-button',
		defaultMessage: 'Show More',
		description: 'Text for the button that loads more items',
	},
});

import { Flex } from '@atlaskit/primitives/compiled';

export interface PaginationProps<TData, TVariables> {
	pageInfo: {
		hasNextPage: boolean;
		endCursor: string | null;
	};
	fetchMore: <K extends keyof TVariables>(
		fetchMoreOptions: FetchMoreQueryOptions<TVariables, K> & FetchMoreOptions<TData, TVariables>,
	) => Promise<ApolloQueryResult<TData>>;
	queryVariables: TVariables;
	connectionPath: string;
}

const getNestedValue = (data: any, path: string): any => {
	return path.split('.').reduce((acc, part) => acc && acc[part], data);
};

export const Pagination = <TData, TVariables extends Record<string, any>>({
	pageInfo,
	fetchMore,
	queryVariables,
	connectionPath,
}: PaginationProps<TData, TVariables>) => {
	const { formatMessage } = useIntl();
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const { hasNextPage, endCursor } = pageInfo;

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore) {
			return;
		}

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					...queryVariables,
					after: endCursor,
				} as TVariables,
				updateQuery: (previousResult: TData, { fetchMoreResult }: { fetchMoreResult?: TData }) => {
					if (!fetchMoreResult) {
						return previousResult;
					}

					const prevConnection = getNestedValue(previousResult, connectionPath);
					const nextConnection = getNestedValue(fetchMoreResult, connectionPath);

					if (!prevConnection || !nextConnection) {
						return fetchMoreResult;
					}

					return {
						...fetchMoreResult,
						[connectionPath]: {
							...nextConnection,
							nodes: [...(prevConnection.nodes || []), ...(nextConnection.nodes || [])],
						},
					};
				},
			});
		} catch (error) {
			// Error handling is delegated to the parent query hook
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, fetchMore, queryVariables, endCursor, connectionPath]);

	if (!hasNextPage) {
		return null;
	}

	return (
		<Flex justifyContent="center">
			<Button isLoading={isLoadingMore} onClick={handleLoadMore}>
				{formatMessage(i18n.showMoreButton)}
			</Button>
		</Flex>
	);
};
